import { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faDollarSign, faEdit, faPercent, faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import dayjs from 'dayjs'
import numeral from 'numeral'

import { Breadcrumb, Table } from 'antd'

import { ApplicationContext } from 'context'

import ButtonPrimary from "components/ButtonPrimary"
import StatTile from "components/StatTile"

import states from 'lib/states'
import premiumRates from 'lib/premium_rates'

import FindPower from "./FindPower"
import ReportDetailsForm from "./ReportDetailsForm"
import PowerForm from "components/PowerForm"
import { DEFAULT_POWER } from './utils'

import { discectError, formatMoney, ucwords } from "lib/utils"
import api from 'lib/api'

import styles from "./styles.module.scss"

const ReportBuilder = ({ settings }) => {
  const { contextAgency, contextError, contextLoading } = useContext(ApplicationContext)
  const [isLoading, setIsLoading] = contextLoading // eslint-disable-line
  const [error, setError] = contextError // eslint-disable-line
  const [agency] = contextAgency // eslint-disable-line
  const [report, setReport] = useState(null)
  const [newPower, setNewPower] = useState(null)
  const [isUpdateReportDetails, setIsUpdateReportDetails] = useState(false)
  const [addingPower, setAddingPower] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [currentReportId, setCurrentReportId] = useState(null)

  const { reportId } = useParams()

  const agencyId = agency.id

  const _fetchReport = useCallback(async () => {
    setIsLoading(true)
    try {
      const { report: newReport } = await api.reports.fetchReport(agencyId, reportId)
      setReport(() => newReport)
      setCurrentReportId(() => reportId)
    } catch(e) {
      setError(discectError(e))
    }
    setIsLoading(false)
  }, [agencyId, reportId, setIsLoading, setError])

  useEffect(() => {
    if (!report || (reportId && currentReportId && reportId !== currentReportId)) _fetchReport()
  }, [reportId, currentReportId, report, _fetchReport])

  // Actions

  const _onUpdateReportStatus = async () => {
    setError(null)
    setIsLoading(true)
    try {
      await api.reports.updateReportStatus(agencyId, reportId, 'REVIEW')
      _fetchReport()
    } catch(e) {
      setError(discectError(e))
      setIsLoading(false)
    }
  }

  const _onSaveReportDetails = async values => {
    setError(null)
    setIsLoading(true)
    try {
      await api.reports.updateReport(agencyId, reportId, values)
      _fetchReport()
      setIsUpdateReportDetails(false)
    } catch(e) {
      setError(discectError(e))
      setIsLoading(false)
    }
  }

  const _onSavePower = async values => {
    values.executionDate = (values.executionDate && values.executionDate !== '') ? dayjs(values.executionDate.replace('_', ' ')).format('YYYY-MM-DD') : null
    values.dob = (values.dob && values.dob !== '') ? dayjs(values.dob.replace('_', ' ')).format('YYYY-MM-DD') : null
    
    setError(null)
    setIsLoading(true)
    try {
      if (editingId) {
        await api.powers.updatePower(agencyId, editingId, values)
      } else {
        await api.report.addPowerToReport(agencyId, reportId, values)
      }
      _fetchReport()
      setNewPower(DEFAULT_POWER)
      setAddingPower(false)
      setEditingId(null)
    } catch(e) {
      setError(discectError(e))
      setIsLoading(false)
    }
  }

  const onEdit = (power) => {
    const params = {
      bondStatus: power.status,
      prefix: power.prefix,
      powerNumber: power.number,
      caseNumber: power.meta_data?.caseNumber || '',
      bondAmount: power.meta_data?.bondAmount || '',
      rate: power.meta_data?.rate || '',
      buff: power.meta_data?.buff || '',
      premium: power.meta_data?.premiumAmount || '',
      executionDate: power.executed_at ? dayjs(power.executed_at).format('MM/DD/YYYY') : '',
      defendantFirst: power.meta_data?.defendantNameParts.nameFirst || '',
      defendantMiddle: power.meta_data?.defendantNameParts?.nameMiddle || '',
      defendantLast: power.meta_data?.defendantNameParts?.nameLast || '',
      ssn: power.meta_data?.ssn || '',
      dob: power.meta_data?.dob ? dayjs(power.meta_data?.dob).format('MM/DD/YYYY') : '',
      writingFirst: power.meta_data?.writingAgent?.nameFirst || '',
      writingLast: power.meta_data?.writingAgent?.nameLast || '',
      courtName: power.meta_data?.courtName || '',
      charge: power.meta_data?.charge || '',
      city: power.meta_data?.city || '',
      state: power.meta_data?.state || '',
      county: power.meta_data?.county || '',
      actualRate: power.meta_data?.actualBuff,
      actualBuff: power.meta_data?.actualRate,
      rateOverride: (power.meta_data?.actualRate !== power.meta_data?.rate),
      buffOverride: (power.meta_data?.actualBuff !== power.meta_data?.buff)
    }
    setNewPower(params)
    setEditingId(power.fb_id)
  }

  const _getTable = (powers, hasTotalsRow) => {
    const columns = [
      {
        title: 'Power Number',
        dataIndex: 'po_number',
        key: 'po_number',
        width: 150,
        align: 'center',
        onCell: ({ po_number, status, meta_data }, index) => {
          console.log(meta_data)
          let className = null
          if (status === 'EXECUTED') className = styles.bg__blue
          if (status === 'VOID') className = styles.bg__red

          let hasNotices = false
          if (status !== 'VOID') {
            if (meta_data.actualBuff !== meta_data.buff) hasNotices = true
            if (meta_data.actualRate !== meta_data.rate) hasNotices = true
          }
          if (hasNotices) {
            className = styles.bg__orange
          }
          return ({
            className: className
          })
        },
        render: (_, { po_number }) => {
          return (
            <div className={styles.tableCellBold}>
              <p>{po_number}</p>
            </div>
          )
        }
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        align: 'center',
        render: (_, { status }) => {
          return (
            <div className={styles.tableCell}>
              <p>{status}</p>
            </div>
          )
        }
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        align: 'center',
        render: (_, { type }) => {
          return (
            <div className={styles.tableCell}>
              <p>{type}</p>
            </div>
          )
        }
      },
      {
        title: 'Execution Date',
        dataIndex: 'executed_at',
        key: 'executed_at',
        align: 'center',
        render: (_, { executed_at }) => {
          return (
            <div className={styles.tableCell}>
              <p>{executed_at ? dayjs(executed_at).format('MM-DD-YYYY') : 'N/A'}</p>
            </div>
          )
        }
      },
      {
        title: 'Liability',
        dataIndex: 'bond_value',
        key: 'bond_value',
        align: 'center',
        render: (_, { meta_data }) => {
          return (
            <div className={styles.tableCell}>
              <p>{meta_data?.bondAmount ? formatMoney(meta_data?.bondAmount) : 0}</p>
            </div>
          )
        }
      },
      {
        title: 'Premium',
        dataIndex: 'premiumAmount',
        key: 'premiumAmount',
        align: 'center',
        render: (_, { meta_data }) => {
          return (
            <div className={styles.tableCell}>
              <p>{meta_data?.premiumAmount ? formatMoney(meta_data.premiumAmount) : 0}</p>
            </div>
          )
        }
      },
      {
        title: 'Rate',
        dataIndex: 'rate',
        key: 'rate',
        align: 'center',
        render: (_, { meta_data }) => {
          return (
            <div className={styles.tableCell}>
              <p>{meta_data?.rate ? formatMoney(meta_data.rate) : 0}</p>
            </div>
          )
        }
      },
      {
        title: 'Buff',
        dataIndex: 'buff',
        key: 'buff',
        align: 'center',
        render: (_, { meta_data }) => {
          return (
            <div className={styles.tableCell}>
              <p>{meta_data?.buff ? formatMoney(meta_data.buff) : 0}</p>
            </div>
          )
        }
      },
      {
        title: 'Case #',
        dataIndex: 'caseNumber',
        key: 'caseNumber',
        align: 'center',
        render: (_, { meta_data }) => {
          return (
            <div className={styles.tableCell}>
              <p>{meta_data?.caseNumber || 'N/A'}</p>
            </div>
          )
        }
      },
      {
        title: 'Defendant Name',
        dataIndex: 'defendantName',
        key: 'defendantName',
        width: 180,
        align: 'center',
        render: (_, { meta_data }) => {
          return (
            <div className={styles.tableCell}>
              <p>{meta_data?.defendantName ? ucwords(meta_data.defendantName.toLowerCase()) : 'N/A'}</p>
            </div>
          )
        }
      },
      {
        title: 'State',
        dataIndex: 'defendantName',
        key: 'defendantName',
        align: 'center',
        render: (_, { meta_data }) => {
          console.log(states)
          return (
            <div className={styles.tableCell}>
              <p>{meta_data?.state ? states[meta_data.state] : 'N/A'}</p>
            </div>
          )
        }
      },
      {
        title: 'County',
        dataIndex: 'county',
        key: 'county',
        align: 'center',
        render: (_, { meta_data }) => {
          return (
            <div className={styles.tableCell}>
              <p>{meta_data?.county || 'N/A'}</p>
            </div>
          )
        }
      },
      {
        title: 'Max',
        dataIndex: 'max',
        key: 'max',
        width: 60,
        align: 'center',
        render: (_, record) => {
          const data = premiumRates(record.meta_data.bondAmount, record.meta_data.premiumAmount, record.meta_data.state, 'STATE')
          return (
            <div className={styles.tableCell}>
              <p>{data?.value ? `$${data.value}` : ''}</p>
            </div>
          )
        }
      },
      {
        title: 'Min',
        dataIndex: 'min',
        key: 'min',
        width: 60,
        align: 'center',
        render: (_, record) => {
          const data = premiumRates(record.meta_data.bondAmount, record.meta_data.premiumAmount, record.meta_data.state, 'STATE')
          return (
            <div className={styles.tableCell}>
              <p>{data?.minimum ? `$${data.minimum}` : ''}</p>
            </div>
          )
        }
      },
      {
        width: 30,
        align: 'center',
        render: (_, record) => {
          return (
            <div className={styles.tableCellActions}>
              <button onClick={() => onEdit(record)}><FontAwesomeIcon icon={faEdit} /></button>
            </div>
          )
        }
      }
    ]
    
    return (
      <Table
        bordered
        rowClassName={(record) => {
          const data = premiumRates(record.bond_value, record.meta_data.premiumAmount, record.meta_data.state, 'STATE')
          if (data && (record.meta_data.premiumAmount > data.value || record.meta_data.premiumAmount < data.minimum)) {
            return styles.tableRowError
          }
          return null
        }}
        dataSource={powers}
        columns={columns}
        size="small"
        pagination={false}
        summary={(pageData) => {
          let totalLiability = 0;
          let totalPremium = 0;
          let totalRate = 0;
          let totalBuff = 0;

          powers.forEach(({ bond_value, meta_data }) => {
            totalLiability += bond_value;
            totalPremium += meta_data.premiumAmount;
            totalRate += meta_data.rate;
            totalBuff += meta_data.buff;
          });

          if (!hasTotalsRow) return null

          return (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} align="center">
                  <div className={styles.tableCellBold}>
                    <p>Total</p>
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} colSpan={3}>
                  &nbsp;
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="center">
                  <div className={styles.tableCell}>
                    <p>{formatMoney(totalLiability)}</p>
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="center">
                  <div className={styles.tableCell}>
                    <p>{formatMoney(totalPremium)}</p>
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="center">
                  <div className={styles.tableCell}>
                    <p>{formatMoney(totalRate)}</p>
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="center">
                  <div className={styles.tableCell}>
                    <p>{formatMoney(totalBuff)}</p>
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} colSpan={7}>
                  &nbsp;
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </>
          );
        }}
      />
    )
  }

  const _getTotalPremium = () => {
    let total = 0
    if (report?.powers)
      report.powers.forEach(itm => total += itm.meta_data.premiumAmount)
    return formatMoney(total)
  }

  const _getTotalLiability = () => {
    let total = 0
    if (report?.powers)
      report.powers.forEach(itm => total += itm.bond_value)
    return formatMoney(total)
  }

  const _getTotalBuff = () => {
    let total = 0
    if (report?.powers)
      report?.powers?.forEach(itm => total += itm.meta_data.buff)
    return formatMoney(total)
  }

  const _getTotalRate = () => {
    let total = 0
    if (report?.powers)
      report.powers.forEach(itm => total += itm.meta_data.rate)
    return formatMoney(total)
  }

  const _onEdit = (power) => {
    const params = {
      id: power.fb_id,
      bondStatus: power.status,
      prefix: power.prefix,
      powerNumber: power.number,
      caseNumber: power.meta_data?.caseNumber || '',
      powerAmount: power.bond_value,
      rate: power.meta_data?.rate || '',
      buff: power.meta_data?.buff || '',
      bondAmount: power.meta_data?.bondAmount || '',
      premium: power.meta_data?.premiumAmount || '',
      executionDate: power.executed_at ? dayjs(power.executed_at).format('MM/DD/YYYY') : '',
      defendantFirst: power.meta_data?.defendantNameParts?.nameFirst || '',
      defendantMiddle: power.meta_data?.defendantNameParts?.nameMiddle || '',
      defendantLast: power.meta_data?.defendantNameParts?.nameLast || '',
      ssn: power.meta_data?.ssn || '',
      dob: power.meta_data?.dob ? dayjs(power.meta_data.dob).format('MM/DD/YYYY') : '',
      writingFirst: power.meta_data?.writingAgent?.nameFirst || '',
      writingLast: power.meta_data?.writingAgent?.nameLast || '',
      state: power.meta_data?.state || '',
      county: power.meta_data?.county || '',
      actualRate: power.meta_data?.actualBuff,
      actualBuff: power.meta_data?.actualRate,
      rateOverride: (power.meta_data?.actualRate !== power.meta_data?.rate),
      buffOverride: (power.meta_data?.actualBuff !== power.meta_data?.buff)
    }
    setNewPower(params)
  }

  if (report) {
    const hasVoids = (report.powers?.filter(itm => itm.status === 'VOID').length > 0)
    return (
      <div className={styles.report}>
        <div className={styles.report__wrapper}>
          <div className={styles.profile__head}>
            <div className={styles.reportList__totals}>
              <div>
                <StatTile mini label="Total Liability" value={_getTotalLiability()} color="navy" icon={<FontAwesomeIcon icon={faDollarSign} />} style={{ width: 210 }} />
                <StatTile mini label="Total Premium" value={_getTotalPremium()} color="navy" icon={<FontAwesomeIcon icon={faDollarSign} />} style={{ width: 210 }} />
                <StatTile mini label="Rate" value={_getTotalRate()} color="grey" icon={<FontAwesomeIcon icon={faPercent} />} style={{ width: 140 }} />
                <StatTile mini label="Buff" value={_getTotalBuff()} color="grey" icon={<FontAwesomeIcon icon={faPercent} />} style={{ width: 140 }} />
              </div>
              <div>
                {(report.status === 'NEW') && <ButtonPrimary color="orange" icon={faCircleCheck} onClick={_onUpdateReportStatus}>Submit for Review</ButtonPrimary>}
              </div>
            </div>
          </div>

          <div className={styles.breadcrumb}>
            <Breadcrumb
              items={[
                {
                  title: <a href="/dashboard">Home</a>,
                },
                {
                  title: <a href="/inventory">Inventory</a>,
                },
                {
                  title: <a href="/inventory/reports">Reports</a>,
                },
                {
                  title: report.name
                },
              ]}
            />
          </div>

          <div className={styles.agencyInfo__wrapper}>
            <div className={styles.agencyInfo__container}>
              <div className={styles.agencyInfo}>
                <div className={styles.agencyInfo__left}>
                  <h4>{report.agencyName}</h4>
                  <p>{report.agencyMetadata.ownerName}</p>
                  <p>{report.agencyMetadata.address}</p>
                  <p>{report.agencyMetadata.state}, {report.agencyMetadata.city} {report.agencyMetadata.zip}</p>
                  <p>{report.agencyMetadata.phone}</p>
                  <p>Liability or Gross: <strong>{report.rate_generator}</strong></p>
                </div>
                <div className={styles.agencyInfo__right}>
                  <div className={styles.agencyInfo__stats}>
                    <StatTile hideViewMore label="Rate" value={`${report.rate}%`} color="blue" icon={<FontAwesomeIcon icon={faPercent} />} style={{ width: 140 }} />
                    <StatTile hideViewMore label="Buff" value={`${report.buff}%`} color="teal" icon={<FontAwesomeIcon icon={faPercent} />} style={{ width: 140 }} />
                    <StatTile hideViewMore label="Minimum" value={'$' + numeral(report.minimum).format('0,0.00')} color="navy" icon={<FontAwesomeIcon icon={faDollarSign} />} style={{ width: 140 }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {report && (
            <>
              <div className={`${styles.report__content} ${styles.reportDetailsSection}`}>
                <div className={styles.reportList}>
                  <h3>
                    Report Details
                    {(report.status !== 'COMPLETE') && <ButtonPrimary small color="navy" icon={faEdit} onClick={()=> setIsUpdateReportDetails(true)}>Edit</ButtonPrimary>}
                  </h3>
                  {isUpdateReportDetails && <ReportDetailsForm report={report} onSave={_onSaveReportDetails} onCancel={() => setIsUpdateReportDetails(false)} />}
                  {!isUpdateReportDetails && (
                    <div className={styles.reportDetails}>
                      <div className={styles.reportDetails__left}>
                        <h4>Report Name: {report.name}</h4>
                        <p>ID: <strong>{report.identifier || 'N/A'}</strong></p>
                        <p>Report Received Date: <strong>{report.received_date ? dayjs(report.received_date).format('MM/DD/YYYY') : 'N/A'}</strong></p>
                      </div>
                      <div className={styles.reportDetails__right}>
                        &nbsp;
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {(!addingPower && !editingId) && (
                <>
                  <div className={`${styles.report__content} ${styles.powerSection}`}>
                    <div className={styles.reportList}>
                      <h3>
                        Report Powers
                        <div className={styles.reportList__header__right}>
                          {(report.status !== 'COMPLETE') && <ButtonPrimary small color="navy" icon={faPlusCircle} onClick={()=> setAddingPower(v => !v)}>Add</ButtonPrimary>}
                        </div>
                      </h3>
                      {_getTable(report.powers.filter(itm => itm.status !== 'VOID'), true)}
                    </div>
                    {hasVoids && (
                      <div className={styles.reportList}>
                        <h3>
                          Voided Powers
                          <div className={styles.reportList__header__right}>
                            &nbsp;
                          </div>
                        </h3>
                        {_getTable(report.powers.filter(itm => itm.status === 'VOID'), false)}
                      </div>
                    )}
                  </div>
                </>
              )}

              {addingPower && (
                <>
                  {!newPower && (
                    <div className={styles.addCase}>
                      <div className={styles.addCase_head}>
                        <h3>Step 1: Choose a Power</h3>
                        <p>Find the power you wish to add to this report and click Select.</p>
                      </div>
                      <FindPower onSelect={p => _onEdit(p)} />
                    </div>
                  )}
                  {newPower && (
                    <div className={styles.addCase}>
                      <div className={styles.addCase_head}>
                        <h3>Step 2: Update Power {`${newPower.prefix}-${newPower.powerNumber}`}</h3>
                        <p>Update the details on the power and click Submit. Required fields are marked with a red asterisk.</p>
                      </div>
                      <PowerForm 
                        className={styles.addCase__form}
                        formClassName={styles.addCase__form}
                        contract={agency.contract}
                        report={report}
                        newPower={newPower}
                        onSave={_onSavePower}
                        onCancel={() => {
                          setNewPower(null)
                        }} 
                      />
                    </div>
                  )}
                </>
              )}

              {editingId && (
                <div className={styles.addCase}>
                  <div className={styles.addCase_head}>
                    <h3>Edit Power</h3>
                  </div>
                  <PowerForm 
                    className={styles.addCase__form}
                    formClassName={styles.addCase__form}
                    contract={agency.contract}
                    report={report}
                    newPower={newPower}
                    onSave={_onSavePower}
                    onCancel={() => {
                      setNewPower(DEFAULT_POWER)
                      setAddingPower(false)
                      setEditingId(null)
                    }} 
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    )
  }
  return null
}

export default ReportBuilder